import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { getPublicSponsors, type PublicSponsor } from "@/lib/sponsors.functions";
import { SponsorLogoCard } from "./SponsorLogoCard";

type Sponsor = PublicSponsor;

export function SponsorStrip() {
  const [sponsors, setSponsors] = useState<Sponsor[] | null>(null);

  useEffect(() => {
    getPublicSponsors()
      .then((data) => setSponsors(data.filter((s) => s.logo_url && s.tier !== "vriend")))
      .catch(() => setSponsors([]));
  }, []);

  if (!sponsors || sponsors.length === 0) return null;

  // Duplicate for seamless infinite scroll. Show higher tiers first.
  const tierRank: Record<string, number> = {
    hoofdpartner: 0,
    premium: 1,
    partner: 2,
    supporter: 3,
    vriend: 4,
  };
  const ordered = [...sponsors].sort(
    (a, b) => (tierRank[a.tier] ?? 99) - (tierRank[b.tier] ?? 99),
  );
  const loop = [...ordered, ...ordered];

  return (
    <section
      aria-label="Onze partners"
      className="border-t border-gold/10 bg-night-deep/40 py-8 md:py-10"
    >
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <div className="flex flex-col items-center gap-2 text-center md:flex-row md:justify-between md:text-left">
          <p className="font-mono-cinema text-[10px] uppercase tracking-[0.3em] text-gold/80">
            Met dank aan onze partners
          </p>
          <Link
            to="/partners"
            className="font-mono-cinema text-[10px] uppercase tracking-[0.25em] text-foreground/60 transition-colors hover:text-gold"
          >
            Word ook partner →
          </Link>
        </div>
        <div
          className="relative mt-6 overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 6%, black 94%, transparent)",
          }}
        >
          <div className="flex w-max animate-sponsor-scroll items-center gap-6 md:gap-8">
            {loop.map((s, i) => (
              <div
                key={`${s.id}-${i}`}
                className={
                  s.tier === "hoofdpartner"
                    ? "w-[220px] md:w-[260px]"
                    : s.tier === "premium"
                      ? "w-[190px] md:w-[220px]"
                      : "w-[170px] md:w-[200px]"
                }
              >
                <SponsorLogoCard sponsor={s} size="sm" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
